import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '../components/Badge.js';
import { EntityTags } from '../components/EntityTags.js';
import { Flag } from '../components/Flag.js';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError, isHttpStatus } from '../lib/http-error.js';
import { ForbiddenPage } from '../components/ForbiddenPage.js';
import {
  clientStatusSchema, formatMinorAmount,
  type ClientOutput, type UpdateClientInput,
  type EngagementOutput, type ComplianceItemOutput,
  ENGAGEMENT_PIPELINE_LABELS, JURISDICTION_LABELS,
  type CreateNoteInput,
  type InvoiceListItem,
} from '@1financial/shared';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { InvoiceFormModal } from '../components/InvoiceFormModal.js';
import { ApplyTemplateModal } from '../components/ApplyTemplateModal.js';
import { ClientFormModal } from '../components/ClientFormModal.js';
import { ViewIcon } from '../components/ActionIcons.js';
import { fmtDate, fmtDateTime } from '../lib/date.js';

const ITEM_STATUS_LABELS: Record<string, string> = {
  Pending: 'Pending',
  InProcess: 'In Process',
  ReadyForReview: 'Ready for Review',
  Reviewed: 'Reviewed',
  SentBack: 'Sent Back',
  SentToClient: 'Sent to Client',
  Completed: 'Completed',
};

type Tab = 'overview' | 'engagements' | 'compliance' | 'invoices' | 'notes';

type ClientNote = {
  id: string;
  scope: string;
  scopeId: string | null;
  title: string | null;
  bodyMarkdown: string;
  authorId: string;
  authorName: string;
  pinned: boolean;
  clientId: string | null;
  engagementId: string | null;
  complianceClientId: string | null;
  complianceItemId: string | null;
  /** OPS 3.8 step 1: checklist-row context on engagement notes. */
  engagementSubItemId: string | null;
  engagementSubItemTitle: string | null;
  createdAt: string;
  updatedAt: string;
};

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { api } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [applyResult, setApplyResult] = useState<string | null>(null);
  // OPS 3.7 step 2: template applied from the "setup complete" banner also
  // flips the client ACTIVE (the retainer fork's Apply path).
  const [applyFromFork, setApplyFromFork] = useState(false);
  const { canDo } = useCapabilities();

  const q = useQuery({
    queryKey: ['client', id],
    enabled: api !== null && !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/clients/${id}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: ClientOutput };
      return body.data;
    },
  });

  const engagements = useQuery({
    queryKey: ['engagements', { clientId: id }],
    enabled: tab === 'engagements' && !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/engagements?clientId=${id}&all=true&limit=100`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: EngagementOutput[]; total: number } };
      return body.data.items;
    },
  });

  const compliance = useQuery({
    queryKey: ['compliance-items', { clientId: id }],
    enabled: tab === 'compliance' && !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/compliance/items?clientId=${id}&limit=100`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: ComplianceItemOutput[]; total: number } };
      return body.data.items;
    },
  });

  const invoices = useQuery({
    queryKey: ['invoices', { clientId: id }],
    enabled: tab === 'invoices' && !!id && canDo('billing.view'),
    queryFn: async () => {
      const res = await fetch(`/v1/invoices?clientId=${id}&limit=100`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: InvoiceListItem[]; total: number } };
      return body.data;
    },
  });

  const notes = useQuery({
    queryKey: ['client-notes', id],
    enabled: (tab === 'notes' || tab === 'overview') && !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/clients/${id}/notes`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: ClientNote[] } };
      return body.data.items;
    },
  });

  const createNote = useMutation({
    mutationFn: async (input: CreateNoteInput) => {
      const res = await fetch('/v1/notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', id] });
    },
  });

  const update = useMutation({
    mutationFn: async (patch: UpdateClientInput | { decision: 'DEFERRED'; engagementId?: string | null }) => {
      const defer = 'decision' in patch;
      const res = await fetch(defer ? `/v1/clients/${id}/retainer` : `/v1/clients/${id}`, {
        method: defer ? 'POST' : 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw await httpError(res);
      return res.json() as Promise<{ data: ClientOutput | { clientStatus: ClientOutput['status']; retainerDecision: 'DEFERRED' } }>;
    },
    onSuccess: (response) => {
      if ('clientStatus' in response.data) {
        const result = response.data;
        queryClient.setQueryData<ClientOutput>(['client', id], (current) => current ? { ...current, status: result.clientStatus, retainerDecision: result.retainerDecision, setupAwaitingRetainer: false } : current);
      }
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setShowEdit(false);
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/clients/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate('/clients');
    },
  });

  if (q.isLoading) return <div className="text-muted">Loading client…</div>;
  if (q.error && isHttpStatus(q.error, 403, 404)) {
    return <ForbiddenPage title="Not found or no access" message="This client doesn't exist or you don't have permission to view it." />;
  }
  if (q.error) return <div className="text-rose-400">{(q.error as Error).message}</div>;
  if (!q.data) return null;

  const c = q.data;

  const clientNotes = (notes.data ?? []).filter((n) => n.scope === 'CLIENT');
  const engagementNotes = (notes.data ?? []).filter((n) => n.scope === 'ENGAGEMENT');
  const complianceNotes = (notes.data ?? []).filter((n) => n.scope === 'COMPLIANCE_ITEM' || n.scope === 'COMPLIANCE_CLIENT');

  return (
    <div className="space-y-6 max-w-4xl">
      <Link to="/clients" className="text-sm text-muted hover:text-white">← All clients</Link>

      <header className="bg-bg-panel border border-border rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              {c.country && <Flag country={c.country} className="text-2xl" />}
              <h1 className="text-2xl font-semibold text-white">{c.name}</h1>
              <ClientStatusBadge status={c.status} />
            </div>
            {c.company && <p className="text-muted mt-1">{c.company}</p>}
            <div className="mt-2">
              <EntityTags scope="CLIENT" entityId={c.id} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canDo('templates.view') && canDo('compliance.edit') && (
              <button
                onClick={() => { setApplyResult(null); setShowApplyTemplate(true); }}
                className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
              >
                Apply template
              </button>
            )}
            <button
              onClick={() => setShowEdit(true)}
              className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
            >
              Edit
            </button>
            <button
              onClick={() => setShowDelete(true)}
              className="text-sm btn-ghost-danger border px-3 py-1.5 rounded-lg"
            >
              Delete
            </button>
          </div>
        </div>
      </header>

      {applyResult && (
        <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center justify-between">
          <span>{applyResult}</span>
          <button onClick={() => setApplyResult(null)} className="text-emerald-300/70 hover:text-emerald-200 ml-3">✕</button>
        </div>
      )}

      {c.setupAwaitingRetainer && (
        <div className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex flex-wrap items-center justify-between gap-2">
          <span>Setup complete — start compliance retainer</span>
          <span className="flex gap-2">
            {canDo('templates.view') && canDo('compliance.edit') && canDo('clients.edit') && (
              <button
                onClick={() => { setApplyResult(null); setApplyFromFork(true); setShowApplyTemplate(true); }}
                className="text-amber-200 hover:text-white border border-amber-500/40 hover:border-accent px-2.5 py-1 rounded-lg text-xs"
              >
                Apply template
              </button>
            )}
            {canDo('clients.edit') && (
              <button
                onClick={() => update.mutate({ decision: 'DEFERRED', engagementId: c.setupEngagementId })}
                disabled={update.isPending}
                className="text-amber-200 hover:text-white border border-amber-500/40 hover:border-accent px-2.5 py-1 rounded-lg text-xs disabled:opacity-50"
              >
                Defer
              </button>
            )}
          </span>
        </div>
      )}

      <div className="border-b border-border">
        <nav className="flex flex-wrap gap-1">
          {(['overview', 'engagements', 'compliance', 'invoices', 'notes'] as Tab[])
            .filter((t) => t !== 'invoices' || canDo('billing.view'))
            .map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium capitalize ${tab === t ? 'text-accent border-b-2 border-accent' : 'text-muted hover:text-white'}`}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && (
        <div className="space-y-6">
          <section className="bg-bg-panel border border-border rounded-xl p-6">
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-muted mb-1">Status</label>
                <select
                  value={c.status}
                  onChange={(e) => update.mutate({ status: e.target.value as ClientOutput['status'] })}
                  disabled={update.isPending}
                  className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent disabled:opacity-50"
                >
                  {clientStatusSchema.options.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            {update.error && (
              <p className="mt-2 text-xs text-rose-400">{(update.error as Error).message}</p>
            )}
          </section>

          <section className="bg-bg-panel border border-border rounded-xl p-6">
            <h2 className="text-sm font-medium text-white mb-4">Details</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <DetailField label="Email"        value={c.email} />
              <DetailField label="Phone"        value={c.phone} />
              <DetailField label="Country"      value={c.country ? <><Flag country={c.country} className="mr-1.5" />{c.country}</> : null} />
              <DetailField label="Industry"     value={c.industry} />
              <DetailField label="Website"      value={c.website ? <a href={c.website} target="_blank" rel="noreferrer" className="text-accent hover:underline">{c.website}</a> : null} />
              <DetailField label="Source"       value={c.source} />
              <DetailField label="Legal name"   value={c.legalName} />
              <DetailField label="UK company number" value={c.companyNumber ? <a href={`https://find-and-update.company-information.service.gov.uk/company/${c.companyNumber}`} target="_blank" rel="noreferrer" className="text-accent hover:underline">{c.companyNumber}</a> : null} />
              <DetailField label="Trade license no." value={c.tradeLicenseNo} />
              <DetailField label="TRN"          value={c.trn} />
              <DetailField label="Incorporation date" value={c.incorporationDate ? fmtDate(c.incorporationDate) : null} />
              <DetailField label="FY end"       value={c.fyEnd ? fmtDate(c.fyEnd) : null} />
              <DetailField label="Jurisdiction" value={c.jurisdiction ? (JURISDICTION_LABELS[c.jurisdiction] ?? c.jurisdiction) : null} />
              <DetailField label="Status"       value={<ClientStatusBadge status={c.status} />} />
              <DetailField label="Created by"   value={c.createdById ? <span className="font-mono text-xs">{c.createdById.slice(0, 8)}…</span> : null} />
              <DetailField label="Updated"        value={fmtDateTime(c.updatedAt)} />
            </dl>
          </section>

          <section className="bg-bg-panel border border-border rounded-xl p-6">
            <h2 className="text-sm font-medium text-white mb-2">Internal notes</h2>
            <p className="text-sm text-white whitespace-pre-wrap">{c.notesMarkdown ?? <span className="text-muted">No notes yet.</span>}</p>
          </section>
        </div>
      )}

      {tab === 'engagements' && (
        <section className="bg-bg-panel border border-border rounded-xl p-6">
          <h2 className="text-sm font-medium text-white mb-4">Engagements</h2>
          <DataTable
            isLoading={engagements.isLoading}
            error={engagements.error as Error | null}
            headers={['Title', 'Jurisdiction', 'Pipeline', 'Start', 'End', 'Actions']}
            rows={(engagements.data ?? []).map((e) => ({
              id: e.id,
              cells: [
                e.title ?? 'Untitled',
                e.jurisdiction ? (JURISDICTION_LABELS[e.jurisdiction] ?? e.jurisdiction) : '—',
                ENGAGEMENT_PIPELINE_LABELS[e.pipeline] ?? e.pipeline,
                e.startDate ? fmtDate(e.startDate) : '—',
                e.endDate ? fmtDate(e.endDate) : '—',
                <ViewIcon to={`/engagements/${e.id}`} label="View engagement" />,
              ],
            }))}
          />
        </section>
      )}

      {tab === 'compliance' && (
        <section className="bg-bg-panel border border-border rounded-xl p-6">
          <h2 className="text-sm font-medium text-white mb-4">Compliance</h2>
          <DataTable
            isLoading={compliance.isLoading}
            error={compliance.error as Error | null}
            headers={['Service', 'Jurisdiction', 'Status', 'Data status', 'End date', 'Due date', 'Actions']}
            rows={(compliance.data ?? []).map((item) => ({
              id: item.id,
              cells: [
                item.serviceType,
                item.jurisdiction ? (JURISDICTION_LABELS[item.jurisdiction] ?? item.jurisdiction) : '—',
                ITEM_STATUS_LABELS[item.status] ?? item.status,
                item.dataStatus ?? '—',
                item.deadline ? fmtDate(item.deadline) : '—',
                item.dueDate ? fmtDate(item.dueDate) : '—',
                <ViewIcon to={`/compliance/${item.id}`} label="View item" />,
              ],
            }))}
          />
        </section>
      )}

      {tab === 'invoices' && (
        <section className="bg-bg-panel border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-white">Invoices</h2>
            {canDo('billing.manage') && (
              <button onClick={() => setShowInvoiceModal(true)} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">+ New invoice</button>
            )}
          </div>
          <DataTable
            isLoading={invoices.isLoading}
            error={invoices.error as Error | null}
            headers={['Number', 'Context', 'Status', 'Issue date', 'Due date', 'Total']}
            rows={(invoices.data?.items ?? []).map((inv) => ({
              id: inv.id,
              cells: [
                <Link to={`/invoices/${inv.id}`} className="text-accent hover:underline">{inv.invoiceNumber}</Link>,
                inv.engagementTitle ?? (inv.engagementId ? 'Engagement' : inv.complianceClientId || inv.recurringPeriodStart ? 'Compliance' : 'Manual'),
                inv.status.replace(/_/g, ' '),
                fmtDate(inv.issueDate),
                fmtDate(inv.dueDate),
                `${formatMinorAmount(inv.total.amount, true)} ${inv.total.currency}`,
              ],
            }))}
          />
        </section>
      )}

      {tab === 'notes' && (
        <section className="bg-bg-panel border border-border rounded-xl p-6">
          <h2 className="text-sm font-medium text-white mb-4">Notes</h2>
          <NoteForm onSubmit={(body) => createNote.mutate({ scope: 'CLIENT', scopeId: id!, bodyMarkdown: body, pinned: false })} />
          {createNote.error && <p className="text-xs text-rose-400 mt-2">{(createNote.error as Error).message}</p>}

          <div className="mt-6 space-y-6">
            <NoteGroup title="Client notes" notes={clientNotes} onSourceClick={() => {}} emptyMessage="No client notes." />
            <NoteGroup
              title="Engagement notes"
              notes={engagementNotes}
              onSourceClick={(note) => note.engagementId && navigate(`/engagements/${note.engagementId}`)}
              emptyMessage="No engagement notes."
            />
            <NoteGroup
              title="Compliance notes"
              notes={complianceNotes}
              onSourceClick={(note) => note.complianceItemId && navigate(`/compliance/${note.complianceItemId}`)}
              emptyMessage="No compliance notes."
            />
          </div>
        </section>
      )}

      {showInvoiceModal && (
        <InvoiceFormModal
          clientId={id!}
          clientCode={c.clientCode}
          onClose={() => setShowInvoiceModal(false)}
        />
      )}

      {showApplyTemplate && (
        <ApplyTemplateModal
          clientId={c.id}
          engagementId={applyFromFork ? c.setupEngagementId ?? undefined : undefined}
          retainerFork={applyFromFork}
          jurisdiction={c.jurisdiction ?? undefined}
          onClose={() => { setShowApplyTemplate(false); setApplyFromFork(false); }}
          onDone={(result) => {
            setShowApplyTemplate(false);
            setApplyFromFork(false);
            setApplyResult(`Template applied — created ${result.created}, skipped ${result.skipped}.`);
            if (result.clientStatus) queryClient.setQueryData<ClientOutput>(['client', id], (current) => current ? { ...current, status: result.clientStatus!, retainerDecision: result.retainerDecision, setupAwaitingRetainer: result.retainerDecision ? false : current.setupAwaitingRetainer } : current);
            queryClient.invalidateQueries({ queryKey: ['client', id] });
            queryClient.invalidateQueries({ queryKey: ['clients'] });
            queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
          }}
        />
      )}

      {showEdit && (
        <ClientFormModal
          mode="edit"
          client={c}
          onClose={() => setShowEdit(false)}
          onSubmit={(patch) => update.mutate(patch)}
          error={update.error?.message ?? null}
          submitting={update.isPending}
        />
      )}

      {showDelete && (
        <ConfirmModal
          title="Delete client"
          message={`This will soft-delete ${c.name}. Linked engagements and compliance items will remain in the database but stop appearing in lists.`}
          onClose={() => setShowDelete(false)}
          onConfirm={() => remove.mutate()}
          error={remove.error?.message ?? null}
          submitting={remove.isPending}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}
    </div>
  );
}

function NoteForm({ onSubmit }: { onSubmit: (body: string) => void }) {
  const [body, setBody] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!body.trim()) return;
        onSubmit(body.trim());
        setBody('');
      }}
      className="space-y-3"
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add a client note…"
        rows={3}
        className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent resize-none"
      />
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!body.trim()}
          className="text-sm bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg font-medium px-4 py-1.5 rounded-lg"
        >
          Add note
        </button>
      </div>
    </form>
  );
}

function NoteGroup({ title, notes, onSourceClick, emptyMessage }: {
  title: string;
  notes: ClientNote[];
  onSourceClick: (note: ClientNote) => void;
  emptyMessage: string;
}) {
  if (notes.length === 0) return null;
  return (
    <div>
      <h3 className="text-xs font-medium text-muted uppercase tracking-wide mb-3">{title}</h3>
      <div className="space-y-3">
        {notes.map((note) => (
          <div key={note.id} className="bg-bg-base border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted">{note.authorName} • {fmtDate(note.createdAt)}</span>
              {note.scope !== 'CLIENT' && note.scopeId && (
                <button
                  onClick={() => onSourceClick(note)}
                  className="text-xs text-accent hover:underline"
                >
                  Open source
                </button>
              )}
            </div>
            {note.title && <h4 className="text-sm font-medium text-white mb-1">{note.title}</h4>}
            {note.engagementSubItemTitle && (
              <div className="text-xs text-accent mb-1">Checklist item: {note.engagementSubItemTitle}</div>
            )}
            <p className="text-sm text-white whitespace-pre-wrap">{note.bodyMarkdown}</p>
          </div>
        ))}
      </div>
      {notes.length === 0 && <p className="text-sm text-muted">{emptyMessage}</p>}
    </div>
  );
}


function DataTable({ headers, rows, isLoading, error }: {
  headers: React.ReactNode[];
  rows: { id: string; cells: React.ReactNode[] }[];
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) return <div className="text-muted p-6 text-center">Loading…</div>;
  if (error) return <div className="text-rose-400 p-6 text-center">{error.message}</div>;
  if (rows.length === 0) return <div className="text-muted p-6 text-center">No records found.</div>;
  return (
    <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
            {headers.map((h, i) => <th key={i} className="px-4 py-3 font-medium">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-border/50 hover:bg-bg-hover/40"
            >
              {row.cells.map((cell, ci) => <td key={ci} className="px-4 py-3 text-white">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-white mt-0.5">{value ?? <span className="text-muted">—</span>}</dd>
    </div>
  );
}

function ClientStatusBadge({ status }: { status: string }) {
  const colour =
    status === 'ACTIVE' ? 'pill-success'
    : status === 'LEAD' ? 'pill-warn'
    : status === 'INACTIVE' ? 'pill-neutral'
    : status === 'CHURNED' ? 'pill-danger'
    : 'pill-neutral';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${colour}`}>{status}</span>;
}


function ConfirmModal({ title, message, onClose, onConfirm, error, submitting, confirmText, confirmClass }: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  error: string | null;
  submitting: boolean;
  confirmText: string;
  confirmClass: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto mx-4 shadow-2xl animate-pop p-5">
        <h2 className="text-base font-semibold text-white mb-2">{title}</h2>
        <p className="text-sm text-muted mb-4">{message}</p>
        {error && <p className="text-xs text-rose-400 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className={`text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50 ${confirmClass}`}
          >
            {submitting ? 'Working…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
